<template>
  <div class="w-full overflow-scroll">
    <h2 class="text-3xl text-center">{{ __('Job operations') }}</h2>
    <div class="mt-5">
      <table class="w-full border-gray-600 border" aria-describedby="Job operations table">
        <tr class="bg-gray-700 text-gray-100 text-left">
          <th id="operation-description" class="p-2 w-full">{{ __('Description') }}</th>
          <th id="operation-price" class="p-2 min-w-40 text-right">{{ __('Price') }}</th>
          <th id="operation-status" class="p-2 min-w-40 text-right">{{ __('Status') }}</th>
          <th id="operation-actions" class="p-2 min-w-32 text-right">{{ __('Actions') }}</th>
        </tr>

        <tr v-for="(operation, index) in job.operations" :key="operation.id" :class="{'bg-gray-300': index%2 === 0, 'align-top': validationError(editForm, `operation.${operation.id}`) || editForm.id === operation.id}">
          <td class="px-2 py-4 align-top">
            <div>
              <span v-if="editForm.id !== operation.id">{{ operation.description }}</span>
              <multiselect v-else class="h-10" :placeholder="__('Operation')" :tag-placeholder="__('Press enter')" v-model="editForm.description" :show-labels="false" :options="operationDescriptions" :taggable="true" @tag="editFormSetOperationDescription"></multiselect>
              <div v-if="validationError('description', `operation.${operation.id}`)" class="text-red-500 text-sm mt-2">{{ validationError('description', `operation.${operation.id}`) }}</div>
            </div>

            <div>
              <label class="text-sm block mb-0 mt-4 font-semibold" v-if="editForm.id === operation.id">{{ __('Observations') }}</label>
              <table-input
                  class="mt-2 italic text-sm"
                  :editable="editForm.id === operation.id"
                  type="text"
                  :placeholder="__('Observations')"
                  v-model="editForm.observations"
                  :initial-value="operation.observations"
                  :input-error="validationError('observations', `operation.${operation.id}`)"/>
            </div>
          </td>
          <td class="px-2 py-4 text-right">
            <table-input
                :editable="editForm.id === operation.id"
                type="number"
                :placeholder="__('Price')"
                v-model="editForm.price"
                :initial-value="operation.price"
                :input-error="validationError('price', `operation.${operation.id}`)"/>
          </td>
          <td class="px-2 py-4 text-right">
            <span v-if="editForm.id !== operation.id" class="operation-status" :class="operation.status">{{ operation.status_label }}</span>
            <div class="relative flex-grow mr-2" v-if="editForm.id === operation.id">
              <select v-model="editForm.status" class="block appearance-none w-full border border-gray-600 text-gray-700 py-2 px-4 pr-8 rounded leading-tight focus:outline-none focus:border-gray-500">
                <option v-for="status in statuses" :key="status.value" :value="status.value">
                  {{ status.label }}
                </option>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
            <div v-if="validationError('status', `operation.${operation.id}`)" class="text-red-500 text-sm mt-2">{{ validationError('status', `operation.${operation.id}`) }}</div>
          </td>

          <td class="px-2 py-4 align-top">
            <div class="flex items-center w-full justify-end">
              <button :disabled="!job.permissions.editOperations" @click="edit(operation)" v-if="operation.id !== editForm.id" class="list-edit-button mr-2">
                <icon name="edit" class="w-4 h-4 fill-current"></icon>
              </button>

              <loading-button :disabled="!job.permissions.editOperations || operation.updating" v-if="operation.id === editForm.id" :loading="operation.updating" @click="update(operation)" class="px-2 py-1 bg-coral-400 text-white cursor-pointer mr-2 py-2 hover:bg-coral-300">
                {{ __('Save') }}
              </loading-button>

              <loading-button :disabled="!job.permissions.editOperations || operation.deleting" :loading="operation.deleting" @click="destroy(operation)" class="list-delete-button" :class="{'bg-coral-400': operation.deleting}">
                <icon name="delete" class="w-4 h-4 fill-current" v-if="!operation.deleting"></icon>
                <span class="-mr-2" v-if="operation.deleting"></span>
              </loading-button>
            </div>
          </td>
        </tr>

        <tr class="bg-gray-700">
          <td class="px-2 py-4 text-white align-top text-right font-bold">
            {{ __('Total') }}: {{ total }}
          </td>
          <td class="px-2 py-4 text-right" colspan="3">
          </td>
        </tr>

        <tr class="bg-gray-800 align-top">
          <td class="px-3 pt-4 pb-2">
            <div>
              <multiselect class="h-10" :placeholder="__('Operation')" :tag-placeholder="__('Press enter')" v-model="createForm.description" :show-labels="false" :options="operationDescriptions" :taggable="true" @tag="createFormSetOperationDescription"></multiselect>
              <div v-if="validationError('description', 'operation')" class="text-red-500 text-sm mt-2 leading-tight">{{ validationError('description', 'operation') }}</div>
            </div>
            <div>
              <label class="text-sm block mb-0 mt-4 font-semibold text-white">{{ __('Observations') }}</label>
              <table-input
                  class="mt-2"
                  type="text"
                  :placeholder="__('Observations')"
                  v-model="createForm.observations"
                  :input-error="validationError('observations', `operation`)"/>
            </div>
          </td>
          <td class="px-3 pt-4 pb-2">
            <table-input
                type="number"
                :placeholder="__('Price')"
                v-model="createForm.price"
                :input-error="validationError('price', `operation`)"/>
          </td>
          <td class="px-3 pt-4 pb-2">
            <div class="relative flex-grow">
              <select placeholder="Part status" v-model="createForm.status" class="h-10 block appearance-none w-full border border-gray-600 text-gray-700 py-2 px-4 pr-8 rounded leading-tight focus:outline-none focus:border-coral-400">
                <option v-for="status in statuses" :key="status.value" :value="status.value">
                  {{ status.label }}
                </option>
              </select>
              <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
            <div v-if="validationError('status', 'operation')" class="text-white text-sm leading-tight">{{ validationError('status', 'operation') }}</div>
          </td>
          <td class="px-3 pt-4 pb-2">
            <loading-button :disabled="!job.permissions.editOperations" :loading="storingOperation" class="h-10 flex items-center w-full justify-center px-2 bg-coral-400 text-white font-medium cursor-pointer hover:bg-coral-300" @click="storeOperation()">
              {{ __('Save') }}
            </loading-button>
          </td>
        </tr>
      </table>
    </div>

  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import LoadingButton from '@/Shared/LoadingButton';
import Icon from '@/Shared/Icon';
import TextInput from '@/Shared/TextInput';
import TableInput from '@/Shared/TableInput';

export default {
  metaInfo() {
    return {
      title: 'Job operations',
    };
  },
  props: {
    job: Object,
    operations: Array,
  },
  components: { Multiselect, LoadingButton, Icon, TextInput, TableInput },
  data() {
    return {
      storingOperation: false,
      operationDescriptions: [],
      statuses: [
        {
          value: 'pending',
          label: this.__('operationStatus.pending'),
        },
        {
          value: 'approved',
          label: this.__('operationStatus.approved'),
        },
        {
          value: 'refused',
          label: this.__('operationStatus.refused'),
        },
      ],
      createForm: {
        description: null,
        observations: null,
        price: null,
        status: 'pending',
      },
      editForm: {
        id: null,
        description: null,
        observations: null,
        price: null,
        status: 'pending',
      },
    };
  },
  mounted() {
    this.operationDescriptions = this.operations.map(o => o.name);
    this.checkOperationErrors();
  },
  methods: {
    checkOperationErrors() {
      this.job.operations.forEach((operation) => {
        if (this.$page.props.errors.hasOwnProperty(`operation.${operation.id}`)) {
          this.editForm = {
            ...operation,
            ...this.$page.props.oldInput,
          };
        }
      });

      if (this.$page.props.errors.hasOwnProperty('operation')) {
        this.createForm = {
          ...this.$page.props.oldInput,
        };
      }
    },

    edit(operation) {
      if (this.editForm) {
        delete this.$page.props.errors[`operations.${this.editForm.id}`];
      }

      this.editForm = { ...operation };
    },
    update(operation) {
      this.$set(operation, 'updating', true);

      let data = {
        ...this.editForm,
        job_id: this.job.id,
      };

      this.$inertia.put(this.route('operations.update', { operation: this.editForm.id }), data, {
        preserveScroll: true,
        preserveState: false,
      });
    },

    destroy(operation) {
      if (confirm(this.__('Delete :description?', { description: operation.description }))) {
        this.$set(operation, 'deleting', true);
        this.$inertia.delete(this.route('operations.destroy', { operation: operation.id }), {
          preserveScroll: true,
          preserveState: false,
        });
      }
    },
    createFormSetOperationDescription(description) {
      let index = this.operationDescriptions.push(description);
      this.createForm.description = this.operationDescriptions[index - 1];
    },
    editFormSetOperationDescription(description) {
      let index = this.operationDescriptions.push(description);
      this.editForm.description = this.operationDescriptions[index - 1];
    },
    storeOperation() {
      this.storingOperation = true;
      this.$inertia.post(this.route('operations.store', { job: this.job.id }), {
        ...this.createForm,
      }, { preserveScroll: true, preserveState: false });
    },
  },
  computed: {
    total() {
      return this.job.operations.reduce(function (a, b) {
        return a + b.price;
      }, 0);
    },
  },
};
</script>
